import Head from 'next/head';
import { useRouter } from 'next/router';

import Layout from '../../components/Layout';
import Hero from '../../components/Home/Hero';
import SquarePattern from '../../components/UI/SquarePattern';
import LargeCards from '../../components/UI/LargeCards';
import MasonryCard from '../../components/UI/MasonryCard';
import Testimonial from '../../components/UI/Testimonial';
import ProductsAnimation from '../../components/Animations/Hero/ProductsAnimation';

import metaRobots from '../../helpers/metaRobots';

import sharecardImage from '../../public/images/sharecard.jpg';
import { Container, Box, Grid, GridItem } from '@chakra-ui/react';
import sitePath from '../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const hero = {
  longText: true,
  smallText: false,
  title: {
    main: 'Tap into global markets. Hire top-tier engineers.',
    highlight: 'Scale quickly.',
    color: '',
  },
  text: 'Our full suite of products supports you the whole way – from AI-powered tech that helps find top talent to HR and people ops support that makes remote management simple.',
  withAnimation: true,
};

const masonryCards = [
  {
    type: 'content_image',
    position: false,
    bgDark: true,
    image: {
      sourceUrl: '/images/home/home-global-teams-management.svg',
      altText: 'Expert recruiters find candidates across the world',
    },
    title: 'The Terminal Talent Hub',
    text: [
      {
        strong: '',
        normal:
          'Get a bird’s eye view of your hiring process, from creating job roles and reviewing candidates, all the way through to offer and remote management. Talent Hub is your homebase for all things global hiring.',
      },
    ],
    button: [
      {
        type: 'light',
        text: {
          normal: 'View Now',
          strong: 'Terminal Talent Hub Demo',
        },
        url: '/products/talent-hub-demo',
      },
    ],
  },
  {
    type: 'content_image',
    position: true,
    bgDark: false,
    image: {
      sourceUrl: '/images/products/recruit.svg',
      altText: 'Expert recruiters find candidates across the world',
    },
    title: 'Recruit',
    text: [
      {
        strong: 'Find the right talent for your growing business needs',
        normal: '',
      },
      {
        strong: '',
        normal:
          'The Terminal Talent Platform offers a remote and diversified global talent pool – 90,000 and growing – to meet your hiring goals, whether you’re looking for one best-in-class engineer or building an all-star talent team from the ground up.',
      },
    ],
  },
  {
    type: 'content_image',
    position: false,
    bgDark: true,
    image: {
      sourceUrl: '/images/products/hire.svg',
      altText: 'Female engineer on computer sends remote messages to her team',
    },
    title: 'Hire',
    text: [
      {
        strong: 'Leverage the power of global recruiting experts and tailor-made recruiting tech',
        normal: '',
      },
      {
        strong: '',
        normal:
          'Let Terminal take on the talent search for you. We provide screening, testing and assessment services – you simply log in to review pre-vetted profiles, interview your most promising applicants, and extend your offers.',
      },
    ],
  },
  {
    type: 'content_image',
    position: true,
    bgDark: false,
    image: {
      sourceUrl: '/images/products/onboard.svg',
      altText: "Example of status bar for an engineer in Terminal's Talent Hub ",
    },
    title: 'Onboard',
    text: [
      {
        strong: 'Have every new hire onboarded headache-free',
        normal: '',
      },
      {
        strong: '',
        normal:
          'Terminal handles the onboarding process for your new team members – including HR, benefits and IT support. Your engineers will be ready to work on day one, and you can take “ship laptop to Chile” off your to-do list.',
      },
    ],
  },
  {
    type: 'content_image',
    position: false,
    bgDark: true,
    image: {
      sourceUrl: '/images/products/manage.svg',
      altText: 'Male engineer on computer sends messages and attends meetings with remote team',
    },
    title: 'Manage',
    text: [
      {
        strong: 'Stay focused on innovating your products with our ongoing HR and admin support',
        normal: '',
      },
      {
        strong: '',
        normal:
          'After onboarding is complete, we continue to deliver everything your new remote employees need, including HR, payroll, and IT services. Our localized teams regularly check in, field questions and ensure that your remote teams are happy and productive.',
      },
    ],
  },
];

const largeCards = [
  {
    image: {
      sourceUrl: '/images/products/terminal-talent-platform.svg',
      altText: 'Engineers from Canada, Mexico and Latin America in our Terminal Talent Platform',
    },
    title: 'The Terminal Talent Platform',
    text: 'Tap into Terminal’s elite global talent pool and find the remote professionals your engineering team needs. Our multi-channel recruiting approach sources candidates from Canada, Europe and Latin America – with a team of experts who handle candidate outreach, pre-screening and contract administration.',
    button: {
      type: 'text-with-arrow',
      text: {
        normal: 'Learn more about the',
        strong: 'Terminal Talent Platform',
      },
      url: '/products/talent-platform',
    },
  },
  {
    image: {
      sourceUrl: '/images/products/terminal-remote-management-platform.svg',
      altText: 'Payroll, HR, and IT support through the Remote Management Platform',
    },
    title: 'The Terminal Remote Management Platform',
    text: 'Scale your engineering team with no risk and zero admin burden. With localized support for your new hires, including HR, benefits and payroll services, you’ll avoid the legal red tape and hidden costs typically associated with international hiring.',
    button: {
      type: 'text-with-arrow',
      text: {
        normal: 'Learn more about the',
        strong: 'Remote Management Platform',
      },
      url: '/products/remote-management-platform',
    },
  },
];

const testimonial = {
  bgDark: false,
  image: {
    sourceUrl: '/images/testimonials/andrew-backes.jpg',
    altText: 'Andrew Backes',
  },
  text: '“With Terminal, we have recruiting and on-the-ground expertise in the markets where we want to hire. We needed a group of people who were experts in the laws in these markets, who could set up payment structures, who would provide an office where engineers could work, and who could handle all the other details for us. Having all that bundled together, that was game-changing.”',
  author: {
    name: 'Andrew Backes',
    jobTitle: 'Head of Engineering at Armory',
  },
};

const footer = {
  longText: false,
  text: 'Find out how Terminal can help you scale and retain engineering talent.',
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function Products() {
  const router = useRouter();

  const meta = {
    title: 'Terminal products: Tap into global markets. Hire top-tier engineers. - Terminal.io',
    description:
      'Our full suite of products supports you the whole way – from AI-powered tech that helps find top talent to HR and people ops support that makes remote management simple.',
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        {hero && (
          <Box bgColor="grey.100">
            <Hero hero={hero}>
              <ProductsAnimation />
            </Hero>
          </Box>
        )}
        {largeCards && largeCards.length && <LargeCards cards={largeCards} />}
        <SquarePattern
          mt={{ base: '0.25rem', lg: '0.75rem' }}
          mb={{ base: '-18.4375rem', lg: '-14.25rem' }}
        />
        {masonryCards && masonryCards.length && (
          <>
            <Grid gap={{ base: '14', lg: '6' }}>
              {masonryCards.map((card) => (
                <GridItem key={card.title}>
                  <MasonryCard card={card} />
                </GridItem>
              ))}
            </Grid>
            <SquarePattern mt={{ base: '-18.75rem', lg: '-14.5rem' }} />
          </>
        )}
        {testimonial && (
          <Container mt={{ base: '14', lg: '24' }}>
            <Testimonial testimonial={testimonial} pt="16" pb="20" px={{ base: '16', lg: '20' }} />
          </Container>
        )}
      </Layout>
    </>
  );
}
